
import React, { useMemo, useState, useEffect } from "react";
import api from "../../api/api"; // adjust path if needed

const NEWLINE = "\n";

// ---- CSV Tools ----
function escapeCSVField(value) {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}
function toCSV(rows, headers) {
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escapeCSVField(r[h])).join(","))
  ].join(NEWLINE);
}
function downloadCSVFrom(headers, rows) {
  if (!rows || rows.length === 0) return;
  const csv = toCSV(rows, headers);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `stock_report_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---- Pivot Logic ----
function buildPivotRows(data, godowns, predicate, minTotal, maxTotal) {
  const byItem = new Map();
  for (const r of data) {
    if (!predicate(r)) continue;
    const key = `${r.ItemNo}||${r.ItemName}`;
    if (!byItem.has(key)) {
      const base = { ItemNo: r.ItemNo, ItemName: r.ItemName, Total: 0 };
      for (const g of godowns) base[g] = 0;
      byItem.set(key, base);
    }
    const row = byItem.get(key);
    row[r.Godown] = (row[r.Godown] || 0) + Number(r.StkQty || 0);
    row.Total += Number(r.StkQty || 0);
  }
  return Array.from(byItem.values()).filter(
    (r) => r.Total >= minTotal && r.Total <= maxTotal
  );
}

const CHIP_CLASSES = [
  "bg-blue-100 text-blue-800 ring-blue-200",
  "bg-emerald-100 text-emerald-800 ring-emerald-200",
  "bg-amber-100 text-amber-800 ring-amber-200",
  "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200",
  "bg-rose-100 text-rose-800 ring-rose-200",
  "bg-indigo-100 text-indigo-800 ring-indigo-200",
];
function chipClassFor(key) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 131 + key.charCodeAt(i)) >>> 0;
  return CHIP_CLASSES[h % CHIP_CLASSES.length];
}
function qtyColor(q) {
  if (q <= 0) return "text-rose-600";
  if (q < 25) return "text-amber-600";
  return "text-emerald-700";
}

// ---- SORT ICON ----
function SortIcon({ active, dir }) {
  return (
    <svg
      className={`h-3.5 w-3.5 transition-opacity ${active ? "opacity-100" : "opacity-30"}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      {dir === 'desc' ? (
        <path d="M7 10l5 5 5-5H7zM7 6h10v2H7z" />
      ) : (
        <path d="M7 14l5-5 5 5H7zM7 6h10v2H7z" />
      )}
    </svg>
  );
}

// ---- MAIN COMPONENT ----
export default function StockReport() {
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");
  const [godown, setGodown] = useState("ALL");
  const [minQty, setMinQty] = useState("");
  const [maxQty, setMaxQty] = useState("");
  const [sort, setSort] = useState(null);
  const [view, setView] = useState("list");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.StockReport("demoUser");
        const rows = res?.Table?.map((r) => ({
          ItemNo: r.ItemNo,
          ItemName: r.ItemDesc,
          Godown: r.gdname,
          StkQty: Number(r.StkQty) || 0
        })) || [];
        setData(rows);
      } catch (err) {
        console.error("Failed to load stock report:", err);
      }
    };
    fetchData();
  }, []);

  const allGodowns = useMemo(
    () => Array.from(new Set(data.map((r) => r.Godown))),
    [data]
  );
  const godowns = useMemo(() => ["ALL", ...allGodowns], [allGodowns]);

  const matchesSearch = (r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [r.ItemNo, r.ItemName, r.Godown].some((v) =>
      String(v).toLowerCase().includes(q)
    );
  };

  const listRows = useMemo(() => {
    if (view !== "list") return [];
    const min = minQty === "" ? -Infinity : Number(minQty);
    const max = maxQty === "" ? Infinity : Number(maxQty);

    let rows = data.filter((r) => {
      const inSearch = matchesSearch(r);
      const inGodown = godown === "ALL" || r.Godown === godown;
      const inQty = r.StkQty >= min && r.StkQty <= max;
      return inSearch && inGodown && inQty;
    });

    if (sort) {
      const { key, dir } = sort;
      rows = [...rows].sort((a, b) => {
        const va = a[key];
        const vb = b[key];
        if (va === vb) return 0;
        if (va > vb) return dir === "asc" ? 1 : -1;
        return dir === "asc" ? -1 : 1;
      });
    }
    return rows;
  }, [view, data, query, godown, minQty, maxQty, sort]);

  const listTotalQty = useMemo(
    () => listRows.reduce((s, r) => s + (Number(r.StkQty) || 0), 0),
    [listRows]
  );

  const pivotRows = useMemo(() => {
    if (view !== "pivot") return [];
    const min = minQty === "" ? -Infinity : Number(minQty);
    const max = maxQty === "" ? Infinity : Number(maxQty);
    let rows = buildPivotRows(data, allGodowns, matchesSearch, min, max);

    if (
      sort &&
      (sort.key === "ItemNo" ||
        sort.key === "ItemName" ||
        sort.key === "StkQty")
    ) {
      const { key, dir } = sort;
      rows = rows.sort((a, b) => {
        const va = key === "StkQty" ? a.Total : a[key];
        const vb = key === "StkQty" ? b.Total : b[key];
        if (va === vb) return 0;
        if (va > vb) return dir === "asc" ? 1 : -1;
        return dir === "asc" ? -1 : 1;
      });
    } else {
      rows = rows.sort((a, b) => a.ItemName.localeCompare(b.ItemName));
    }
    return rows;
  }, [view, data, query, minQty, maxQty, sort, allGodowns]);

  const pivotTotalQty = useMemo(
    () => pivotRows.reduce((s, r) => s + (Number(r.Total) || 0), 0),
    [pivotRows]
  );

  const totalRows = view === 'list' ? listRows.length : pivotRows.length;
  const totalQty = view === 'list' ? listTotalQty : pivotTotalQty;

  function toggleSort(key) {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }

  function exportCurrent() {
    if (view === 'list') {
      const headers = ["ItemNo", "ItemName", "Godown", "StkQty"];
      downloadCSVFrom(headers, listRows);
    } else {
      const headers = ["ItemNo", "ItemName", ...allGodowns, "Total"];
      downloadCSVFrom(headers, pivotRows);
    }
  }

  return (
    <div className="min-h-screen p-3 bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 sm:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="w-full text-center mb-6 text-2xl sm:text-3xl font-bold text-gray-800">
            Stock Availability
          </h1>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 px-2 py-1 border shadow-sm rounded-xl bg-white/80 backdrop-blur">
              <span className="text-xs text-gray-600">View</span>
              <button onClick={() => setView('list')}
                className={`px-2 py-1 text-xs rounded-lg transition ${view==='list' ? "bg-indigo-600 text-white shadow" : "hover:bg-gray-100"}`}>List</button>
              <button onClick={() => setView('pivot')}
                className={`px-2 py-1 text-xs rounded-lg transition ${view==='pivot' ? "bg-fuchsia-600 text-white shadow" : "hover:bg-gray-100"}`}>Pivot</button>
            </div>
            <button
              onClick={exportCurrent}
              className="inline-flex items-center px-3 py-2 text-sm font-medium border border-indigo-200 shadow-sm rounded-xl bg-white/90 hover:bg-indigo-50"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats
        <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4">
          <div className="p-3 bg-white shadow-sm rounded-2xl ring-1 ring-indigo-100">
            <div className="text-xs text-gray-500">Items</div>
            <div className="text-xl font-semibold">{totalRows}</div>
          </div>
          <div className="p-3 bg-white shadow-sm rounded-2xl ring-1 ring-emerald-100">
            <div className="text-xs text-gray-500">Total Qty</div>
            <div className="text-xl font-semibold text-emerald-700">{totalQty}</div>
          </div>
          <div className="hidden p-3 bg-white shadow-sm sm:block rounded-2xl ring-1 ring-amber-100">
            <div className="text-xs text-gray-500">Godowns</div>
            <div className="text-xl font-semibold">{allGodowns.length}</div>
          </div>
          <div className="hidden p-3 bg-white shadow-sm sm:block rounded-2xl ring-1 ring-fuchsia-100">
            <div className="text-xs text-gray-500">View</div>
            <div className="text-xl font-semibold">{view === 'list' ? 'List' : 'Pivot'}</div>
          </div>
        </div> */}

        {/* Toolbar */}
        <div className="grid grid-cols-1 gap-3 mb-4 sm:grid-cols-12">
          <div className="sm:col-span-6">
            <label className="block mb-1 text-sm font-medium text-gray-900">Search</label>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search item no / name / godown..."
              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 shadow-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="sm:col-span-3">
            <label className={`className="block mb-1 text-sm font-medium text-gray-900 ${view==='pivot' ? "text-gray-400" : "text-gray-600"}`}>
              Godown (list view)
            </label>
            <select
              value={godown}
              onChange={e => setGodown(e.target.value)}
              disabled={view==='pivot'}
              className={`w-full rounded-xl border bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 ${view==='pivot' ? "border-gray-200 text-gray-400" : "border-gray-300 focus:ring-fuchsia-500"}`}
            >
              {godowns.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          {/* Qty filter */}
          <div className="sm:col-span-1">
            <label className="block mb-1 text-xs font-semibold text-gray-900">Min Qty{view==='pivot' && ' (Total)'}</label>
            <input
              type="number"
              inputMode="numeric"
              value={minQty}
              onChange={e => setMinQty(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 shadow-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="block mb-1 text-xs font-semibold text-gray-900">Max Qty{view==='pivot' && ' (Total)'}</label>
            <input
              type="number"
              inputMode="numeric"
              value={maxQty}
              onChange={e => setMaxQty(e.target.value)}
              placeholder="∞"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 shadow-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Summary */}
        <div className="mb-3 text-sm text-gray-700">
          Showing <span className="font-semibold">{totalRows}</span> {view==='list' ? 'rows' : 'items'} · Total Qty: <span className="font-semibold">{totalQty}</span>
        </div>

        {/* MOBILE CARDS */}
        {view === 'list' && (
          <>
            <div className="space-y-3 sm:hidden">
              {listRows.length === 0 && (
                <div className="p-4 text-center text-gray-500 bg-white border border-gray-200 rounded-2xl">No matching records</div>
              )}
              {listRows.map((r, idx) => (
                <div key={idx} className="p-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{r.ItemName}</div>
                    <div className={`rounded-full px-2 py-0.5 text-xs ring-1 ${chipClassFor(r.Godown)}`}>{r.Godown}</div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{r.ItemNo}</div>
                  <div className={`mt-2 text-lg font-semibold tabular-nums ${qtyColor(r.StkQty)}`}>{r.StkQty}</div>
                </div>
              ))}
            </div>
            {/* DESKTOP TABLE */}
            <div className="hidden overflow-hidden bg-white border border-gray-200 shadow-sm sm:block rounded-2xl">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs tracking-wider text-left uppercase bg-gray-100">
                    <tr>
                      {[
                        { key: "ItemNo", label: "Item No" },
                        { key: "ItemName", label: "Item Name" },
                        { key: "Godown", label: "Godown" },
                        { key: "StkQty", label: "Stock Qty" },
                      ].map(col => (
                        <th
                          key={col.key}
                          onClick={() => toggleSort(col.key)}
                          className="px-4 py-3 cursor-pointer select-none"
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>{col.label}</span>
                            <SortIcon active={sort?.key === col.key} dir={sort?.dir} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-gray-500">No matching records</td>
                      </tr>
                    )}
                    {listRows.map((r, idx) => (
                      <tr key={idx} className={`${idx % 2 ? "bg-white" : "bg-indigo-50/30"} hover:bg-fuchsia-50/60`}>
                        <td className="px-4 py-2 font-mono">{r.ItemNo}</td>
                        <td className="px-4 py-2">{r.ItemName}</td>
                        <td className="px-4 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs ring-1 ${chipClassFor(r.Godown)}`}>{r.Godown}</span>
                        </td>
                        <td className={`px-4 py-2 text-right tabular-nums font-semibold ${qtyColor(r.StkQty)}`}>{r.StkQty}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={3} className="px-4 py-2 font-bold text-right">Total</td>
                      <td className="px-4 py-2 font-bold text-right text-emerald-700">{listTotalQty}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        {/* PIVOT VIEW */}
        {view === 'pivot' && (
          <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs tracking-wider text-left uppercase bg-gray-100">
                  <tr>
                    <th className="px-4 py-3">Item No</th>
                    <th className="px-4 py-3">Item Name</th>
                    {allGodowns.map(g => (
                      <th key={g} className="px-4 py-3 text-right">{g}</th>
                    ))}
                    <th className="px-4 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {pivotRows.length === 0 && (
                    <tr>
                      <td colSpan={2 + allGodowns.length + 1} className="px-4 py-6 text-center text-gray-500">No matching items</td>
                    </tr>
                  )}
                  {pivotRows.map((r, idx) => (
                    <tr key={r.ItemNo + idx} className={`${idx % 2 ? "bg-white" : "bg-indigo-50/30"} hover:bg-fuchsia-50/60`}>
                      <td className="px-4 py-2 font-mono">{r.ItemNo}</td>
                      <td className="px-4 py-2">{r.ItemName}</td>
                      {allGodowns.map(g => {
                        const val = r[g] ?? 0;
                        return (
                          <td key={g} className={`px-4 py-2 text-right tabular-nums ${qtyColor(val)}`}>{val}</td>
                        );
                      })}
                      <td className={`px-4 py-2 text-right font-semibold tabular-nums ${qtyColor(r.Total)}`}>{r.Total}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={2 + allGodowns.length - 1} className="px-4 py-2 font-bold text-right">Total</td>
                    <td className="px-4 py-2 font-bold text-right text-emerald-700">{pivotTotalQty}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Tips */}
        <div className="mt-3 text-xs text-gray-500">
          {view==='list' ?
            <span>Tip: Click a column header to sort. Click twice to reverse, third time to clear.</span> :
            <span>Pivot view shows Item-wise totals. Qty filters apply to <strong>Total</strong>. Export includes godown columns.</span>
          }
        </div>
      </div>
    </div>
  );
}
